/* 2nu activity selector shared logic. English-base source; UI copy is supplied by Liquid locale keys. */
(function twoNuActivityCoreFactory(root, factory) {
  "use strict";

  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.TwoNuActivityCore = api;
})(typeof globalThis !== "undefined" ? globalThis : this, function buildTwoNuActivityCore() {
  "use strict";

  const MIN_SELECTIONS = 1;
  const MAX_SELECTIONS = 3;

  const TAXONOMY = Object.freeze([
    Object.freeze({ id: "running", scoreKey: "running", pathSegment: "running" }),
    Object.freeze({ id: "hiking", scoreKey: "trail", pathSegment: "hiking" }),
    Object.freeze({ id: "water-sports", scoreKey: "water", pathSegment: "water-sports" }),
    Object.freeze({ id: "fishing", scoreKey: "water", pathSegment: "fishing" }),
    Object.freeze({ id: "cycling", scoreKey: "cycling", pathSegment: "cycling" }),
    Object.freeze({ id: "driving", scoreKey: "driving", pathSegment: "driving" }),
    Object.freeze({ id: "golf", scoreKey: "golf", pathSegment: "golf" }),
    Object.freeze({ id: "everyday", scoreKey: "everyday", pathSegment: "everyday" }),
    Object.freeze({ id: "alpine", scoreKey: "alpine", pathSegment: "high-mountain-trekking" }),
  ]);

  const ACTIVITY_ORDER = Object.freeze(TAXONOMY.map((activity) => activity.id));
  const ORDER_INDEX = new Map(ACTIVITY_ORDER.map((id, index) => [id, index]));
  const SCORE_KEY_BY_ACTIVITY = Object.freeze(
    Object.fromEntries(TAXONOMY.map((activity) => [activity.id, activity.scoreKey]))
  );
  const PATH_SEGMENT_BY_ACTIVITY = Object.freeze(
    Object.fromEntries(TAXONOMY.map((activity) => [activity.id, activity.pathSegment]))
  );

  const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") return value.split(",");
    return [];
  };

  const canonicalizeActivityIds = (value, options) => {
    const settings = Object.assign(
      { min: MIN_SELECTIONS, max: MAX_SELECTIONS, strict: false },
      options || {}
    );
    const seen = new Set();
    const selected = [];

    toArray(value).forEach((rawId) => {
      const id = String(rawId || "").trim().toLowerCase();
      if (!ORDER_INDEX.has(id) || seen.has(id)) return;
      seen.add(id);
      selected.push(id);
    });

    selected.sort((a, b) => ORDER_INDEX.get(a) - ORDER_INDEX.get(b));

    if (settings.strict && (selected.length < settings.min || selected.length > settings.max)) {
      throw new RangeError(
        `Expected ${settings.min}-${settings.max} unique activities; received ${selected.length}.`
      );
    }

    return selected.slice(0, settings.max);
  };

  const routeKeyFor = (activityIds) => canonicalizeActivityIds(
    activityIds,
    { min: MIN_SELECTIONS, max: MAX_SELECTIONS, strict: true }
  ).join("__");

  const scoreKeysFor = (activityIds) => {
    const keys = [];
    canonicalizeActivityIds(
      activityIds,
      { min: MIN_SELECTIONS, max: MAX_SELECTIONS, strict: true }
    ).forEach((id) => {
      const scoreKey = SCORE_KEY_BY_ACTIVITY[id];
      if (scoreKey && !keys.includes(scoreKey)) keys.push(scoreKey);
    });
    return keys;
  };

  const hasSharedWaterSelection = (activityIds) => {
    const selected = canonicalizeActivityIds(
      activityIds,
      { min: MIN_SELECTIONS, max: MAX_SELECTIONS, strict: true }
    );
    return selected.includes("water-sports") && selected.includes("fishing");
  };

  const isWaterFishingPair = (activityIds) => {
    const selected = canonicalizeActivityIds(
      activityIds,
      { min: MIN_SELECTIONS, max: MAX_SELECTIONS, strict: true }
    );
    return selected.length === 2 && hasSharedWaterSelection(selected);
  };

  const finiteNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const thresholdSource = (destination) => {
    if (!destination || typeof destination !== "object") return {};
    if (destination.threshold) return destination.threshold;
    if (destination.matchPolicy) return destination.matchPolicy;
    if (destination.matching?.threshold) return destination.matching.threshold;
    if (destination.recommendation) {
      return {
        minTotal: destination.recommendation.minimumTotal,
        minEach: destination.recommendation.minimumPerAxis,
      };
    }
    return {};
  };

  const defaultThresholdFor = (_selectedActivityIds, scoreKeysDeduped) => {
    const uniqueAxisCount = scoreKeysDeduped.length;
    if (uniqueAxisCount === 1) return { minTotal: 4, minEach: 4 };
    if (uniqueAxisCount === 2) return { minTotal: 8, minEach: 0 };
    return {
      minTotal: uniqueAxisCount * 4,
      minEach: uniqueAxisCount === 3 ? 3 : 0,
    };
  };

  const policyFor = (activityIds, destination) => {
    const selectedActivityIds = canonicalizeActivityIds(
      activityIds,
      { min: MIN_SELECTIONS, max: MAX_SELECTIONS, strict: true }
    );
    const scoreKeysDeduped = scoreKeysFor(selectedActivityIds);
    const defaults = defaultThresholdFor(selectedActivityIds, scoreKeysDeduped);
    const threshold = thresholdSource(destination);
    const configuredMinTotal = finiteNumber(threshold.minTotal);
    const configuredMinEach = finiteNumber(threshold.minEach);

    return Object.freeze({
      selectedActivityIds: Object.freeze(selectedActivityIds.slice()),
      scoreKeysDeduped: Object.freeze(scoreKeysDeduped.slice()),
      hasSharedWaterSelection: hasSharedWaterSelection(selectedActivityIds),
      isWaterFishingPair: isWaterFishingPair(selectedActivityIds),
      minTotal: configuredMinTotal === null ? defaults.minTotal : configuredMinTotal,
      minEach: configuredMinEach === null
        ? defaults.minEach
        : Math.max(defaults.minEach, configuredMinEach),
      maxTotal: scoreKeysDeduped.length * 5,
    });
  };

  const ruleFor = (destination) => {
    if (!destination || typeof destination !== "object") return {};
    return destination.eligibility || destination.productRule || destination.matching?.eligibility || {};
  };

  const normalizedStringSet = (value) => new Set(
    toArray(value).map((item) => String(item || "").trim()).filter(Boolean)
  );

  const variantIsEligible = (variant, destination) => {
    if (!variant || variant.available !== true || variant.eligible === false || variant.matchEligible === false) {
      return false;
    }

    const rule = ruleFor(destination);
    const variantId = String(variant.id || "");
    const sku = String(variant.sku || variant.shopifySku || "");
    const families = Array.isArray(variant.families) ? variant.families.map(String) : [];
    const allowedIds = normalizedStringSet(rule.eligibleVariantIds || rule.allowVariantIds);
    const deniedIds = normalizedStringSet(rule.ineligibleVariantIds || rule.excludeVariantIds);
    const allowedSkus = normalizedStringSet(rule.eligibleSkus || rule.allowSkus);
    const deniedSkus = normalizedStringSet(rule.ineligibleSkus || rule.excludeSkus);
    const deniedFamilies = normalizedStringSet(rule.excludeFamilies);

    if (allowedIds.size && !allowedIds.has(variantId)) return false;
    if (allowedSkus.size && !allowedSkus.has(sku)) return false;
    if (deniedIds.has(variantId) || deniedSkus.has(sku)) return false;
    if (families.some((family) => deniedFamilies.has(family))) return false;
    return true;
  };

  const scoreVariant = (variant, activityIds, destination, stableOrder) => {
    if (!variantIsEligible(variant, destination)) return null;

    const policy = policyFor(activityIds, destination);
    const rawScores = variant.activityScores;
    if (!rawScores || typeof rawScores !== "object" || Array.isArray(rawScores)) return null;

    const scores = {};
    for (const key of policy.scoreKeysDeduped) {
      const value = finiteNumber(rawScores[key]);
      if (value === null) return null;
      scores[key] = Math.max(0, Math.min(5, value));
    }

    const selectedValues = policy.scoreKeysDeduped.map((key) => scores[key]);
    const total = selectedValues.reduce((sum, score) => sum + score, 0);
    const minSelectedScore = Math.min(...selectedValues);
    const score5Count = selectedValues.filter((score) => score === 5).length;
    const qualifies = total >= policy.minTotal
      && selectedValues.every((score) => score >= policy.minEach);
    if (!qualifies) return null;

    return Object.freeze({
      variant,
      scores: Object.freeze(scores),
      total,
      minSelectedScore,
      score5Count,
      stableOrder: Number.isFinite(Number(stableOrder))
        ? Number(stableOrder)
        : Number.MAX_SAFE_INTEGER,
      policy,
    });
  };

  const compareRanked = (a, b) => (
    b.minSelectedScore - a.minSelectedScore
    || b.total - a.total
    || b.score5Count - a.score5Count
    || a.stableOrder - b.stableOrder
  );

  const rankVariants = (variants, activityIds, destination) => (
    Array.isArray(variants) ? variants : []
  )
    .map((variant, index) => scoreVariant(variant, activityIds, destination, index))
    .filter(Boolean)
    .sort(compareRanked);

  const destinationEntries = (registry) => {
    const destinations = registry?.destinations;
    if (Array.isArray(destinations)) return destinations;
    if (destinations && typeof destinations === "object") {
      return Object.entries(destinations).map(([routeKey, destination]) => (
        Object.assign({ routeKey }, destination)
      ));
    }
    return [];
  };

  const destinationFor = (registry, activityIds) => {
    const routeKey = routeKeyFor(activityIds);
    return destinationEntries(registry).find((destination) => destination?.routeKey === routeKey) || null;
  };

  const appendQuery = (url, parameter, value) => {
    const source = String(url || "");
    const hashIndex = source.indexOf("#");
    const hash = hashIndex >= 0 ? source.slice(hashIndex) : "";
    const base = hashIndex >= 0 ? source.slice(0, hashIndex) : source;
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${encodeURIComponent(parameter)}=${encodeURIComponent(value)}${hash}`;
  };

  const resolveDestinationUrl = (registry, activityIds) => {
    const selected = canonicalizeActivityIds(
      activityIds,
      { min: MIN_SELECTIONS, max: MAX_SELECTIONS, strict: true }
    );
    const routeKey = routeKeyFor(selected);
    const destination = destinationFor(registry, selected);
    const directUrl = destination?.url || destination?.path || destination?.canonicalUrl;
    if (directUrl && destination?.status !== "placeholder") {
      return Object.freeze({ url: directUrl, routeKey, destination, usedFallback: false });
    }

    const fallback = registry?.fallback || {};
    const fallbackUrl = fallback.url || fallback.path || "/pages/activity-match";
    const parameter = fallback.queryParam || "activities";
    return Object.freeze({
      url: appendQuery(fallbackUrl, parameter, selected.join(",")),
      routeKey,
      destination,
      usedFallback: true,
    });
  };

  const enumerateCombinations = (options) => {
    const settings = Object.assign({ min: 1, max: MAX_SELECTIONS }, options || {});
    const combinations = [];

    const visit = (startIndex, selected) => {
      if (selected.length >= settings.min) combinations.push(Object.freeze(selected.slice()));
      if (selected.length >= settings.max) return;
      for (let index = startIndex; index < ACTIVITY_ORDER.length; index += 1) {
        selected.push(ACTIVITY_ORDER[index]);
        visit(index + 1, selected);
        selected.pop();
      }
    };

    visit(0, []);
    return Object.freeze(combinations);
  };

  const enumeratePhaseOneCombinations = () => {
    const combinations = ACTIVITY_ORDER.map((id) => Object.freeze([id]));
    for (let first = 0; first < ACTIVITY_ORDER.length; first += 1) {
      for (let second = first + 1; second < ACTIVITY_ORDER.length; second += 1) {
        combinations.push(Object.freeze([ACTIVITY_ORDER[first], ACTIVITY_ORDER[second]]));
      }
    }
    return Object.freeze(combinations);
  };
  const enumerateTripleCombinations = () => enumerateCombinations({ min: 3, max: 3 });
  const enumerateAllCombinations = () => Object.freeze([
    ...enumeratePhaseOneCombinations(),
    ...enumerateTripleCombinations(),
  ]);

  const plannedPagePathFor = (activityIds) => {
    const selected = canonicalizeActivityIds(
      activityIds,
      { min: 3, max: 3, strict: true }
    );
    return `/pages/${selected.map((id) => PATH_SEGMENT_BY_ACTIVITY[id]).join("-")}-sunglasses`;
  };

  return Object.freeze({
    MIN_SELECTIONS,
    MAX_SELECTIONS,
    TAXONOMY,
    ACTIVITY_ORDER,
    SCORE_KEY_BY_ACTIVITY,
    PATH_SEGMENT_BY_ACTIVITY,
    canonicalizeActivityIds,
    routeKeyFor,
    scoreKeysFor,
    hasSharedWaterSelection,
    isWaterFishingPair,
    policyFor,
    variantIsEligible,
    scoreVariant,
    compareRanked,
    rankVariants,
    destinationFor,
    resolveDestinationUrl,
    enumerateCombinations,
    enumeratePhaseOneCombinations,
    enumerateTripleCombinations,
    enumerateAllCombinations,
    plannedPagePathFor,
  });
});
